/**
 * Project progress bar.
 *
 * Visual encoding of where a project's parts sit across the four phase
 * buckets. Used by the kanban card, dashboard "Your projects" tile, and
 * the project detail header so the same mental model is reinforced at
 * every surface.
 *
 * Bucket colour ramp (gray → accent → accent-strong → success), chosen so
 * the bar reads left-to-right as "progress" intuitively without relying on
 * the user remembering a legend:
 *
 *   Procurement  border-strong   (subtle gray)
 *   Inspection   accent          (the app's slate-blue)
 *   Billing      accent-strong   (a deeper shade — "almost there")
 *   Closed       success         (green — money in the bank)
 *
 * Two variants:
 *   compact — single 4px bar for kanban cards / dashboard tiles
 *   full    — 6px bar plus a four-segment numeric breakdown below for the
 *             project detail header
 *
 * For projects with zero parts the bar renders as an empty track with a
 * "No parts" label so the slot doesn't shift around when parts are added.
 */

import type { ProjectProgressBuckets } from '@procurement/shared';
import { totalPartsInBuckets } from '@procurement/shared';
import { cn } from '@/lib/cn';

interface Props {
  buckets: ProjectProgressBuckets;
  variant?: 'compact' | 'full';
  className?: string;
}

interface SegmentSpec {
  key: keyof ProjectProgressBuckets;
  label: string;
  /** Tailwind background class. */
  bg: string;
}

const SEGMENTS: ReadonlyArray<SegmentSpec> = [
  { key: 'procurement', label: 'Procurement', bg: 'bg-border-strong' },
  { key: 'inspection', label: 'Inspection', bg: 'bg-accent' },
  { key: 'billing', label: 'Billing', bg: 'bg-accent-strong' },
  { key: 'closed', label: 'Closed', bg: 'bg-success' },
];

export function ProjectProgressBar({ buckets, variant = 'compact', className }: Props) {
  const total = totalPartsInBuckets(buckets);
  const isFull = variant === 'full';
  const trackHeight = isFull ? 'h-1.5' : 'h-1';

  if (total === 0) {
    return (
      <div className={cn('flex flex-col gap-1', className)}>
        <div className={cn('w-full rounded-full bg-subtle', trackHeight)} />
        <span className="text-[10px] text-tertiary">No parts</span>
      </div>
    );
  }

  // Tooltip describes the full distribution — same data the "full" variant
  // shows numerically. Compact-variant users get this on hover.
  const tooltip = SEGMENTS.map((s) => `${s.label}: ${buckets[s.key]}`).join(' · ');

  return (
    <div className={cn('flex flex-col gap-1', className)} title={tooltip}>
      <div
        className={cn(
          'flex w-full overflow-hidden rounded-full bg-subtle',
          trackHeight,
        )}
        role="img"
        aria-label={`Project progress: ${tooltip}`}
      >
        {SEGMENTS.map((s) => {
          const count = buckets[s.key];
          if (count === 0) return null;
          const pct = (count / total) * 100;
          return (
            <div
              key={s.key}
              className={cn('h-full', s.bg)}
              style={{ width: `${pct}%` }}
            />
          );
        })}
      </div>
      {isFull ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-secondary tabular-nums">
          {SEGMENTS.map((s) => (
            <span key={s.key} className="inline-flex items-center gap-1">
              <span className={cn('h-2 w-2 rounded-sm', s.bg)} aria-hidden />
              {s.label}: {buckets[s.key]}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
